import MemoizedIcon from '@components/MemoizedIcon';
import { Dialog, Transition } from '@headlessui/react';
import xIcon from '@iconify/icons-heroicons-outline/x';
import React, { Fragment } from 'react';
import { twMerge } from 'tailwind-merge';

export enum Position {
  Left = 'Left',
  Right = 'Right',
}
interface DrawerProps {
  childKey?: string;
  drawerHeader: React.ReactNode;
  children?: React.ReactNode;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onClose?: () => void;
  defaultPadding?: boolean;
  defaultVerticalPadding?: boolean;
  dataTestId?: string;
  position?: Position;
  withHeaderSeparator?: boolean;
}

const TRANSITION = {
  [Position.Left]: {
    enterFrom: '-translate-x-full',
    enterTo: 'translate-x-0',
    leaveFrom: 'translate-y-0',
    leaveTo: '-translate-x-full',
  },
  [Position.Right]: {
    enterFrom: 'translate-x-full',
    enterTo: 'translate-x-0',
    leaveFrom: 'translate-y-0',
    leaveTo: 'translate-x-full',
  },
};

const DrawerLayout = ({
  drawerHeader,
  children,
  isOpen,
  setIsOpen,
  childKey = '',
  onClose,
  defaultPadding = true,
  defaultVerticalPadding = true,
  dataTestId = 'drawer-layout',
  position = Position.Right,
  withHeaderSeparator = false,
}: DrawerProps) => {
  const closeModal = () => {
    setIsOpen(false);
    onCloseEvent();
  };

  const onCloseEvent = () => {
    onClose?.();
  };

  return (
    <Transition.Root show={isOpen} as={Fragment} key={childKey}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={onCloseEvent}
        data-testid={dataTestId}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-in-out duration-75"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in-out duration-75"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity  overflow-hidden" />
        </Transition.Child>
        <div className="fixed inset-0 overflow-hidden" key="Transition-box">
          <div
            data-testid="drawer"
            className={twMerge(
              'pointer-events-none fixed inset-y-0 flex max-w-md',
              position === Position.Right
                ? 'right-0 sm:pl-10'
                : 'left-0 sm:pr-10',
            )}
          >
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-75 sm:duration-700"
              leave="transform transition ease-in-out duration-75 sm:duration-700"
              {...TRANSITION[position]}
            >
              <Dialog.Panel className="pointer-events-auto relative w-screen sm:max-w-md">
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-75"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-75"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute top-0 left-0 -ml-8 flex pt-4 pr-2 sm:-ml-10 sm:pr-4"></div>
                </Transition.Child>
                <div
                  className={twMerge(
                    'flex h-full flex-col overflow-y-auto bg-white shadow-xl',
                    defaultPadding && 'px-5',
                    defaultVerticalPadding && 'pt-4 pb-10',
                  )}
                >
                  <div>
                    <Dialog.Title>
                      <div
                        className={twMerge(
                          'w-auto flex items-center',
                          withHeaderSeparator && 'border-b',
                        )}
                      >
                        <div
                          data-testid="drawer-header"
                          className="grow"
                          key="header-div"
                        >
                          {drawerHeader}
                        </div>
                        <div
                          className={twMerge(
                            'title-text ml-auto flex items-center',
                            !defaultPadding && 'pr-5',
                          )}
                          key="button-div"
                        >
                          <button
                            type="button"
                            className="rounded-md text-black focus:outline-none focus:ring-2 focus:ring-white"
                            onClick={() => closeModal()}
                            data-testid="close-drawer-btn"
                          >
                            <MemoizedIcon icon={xIcon} className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    </Dialog.Title>
                  </div>
                  <div data-testid="drawer-content" className="h-full mt-2">
                    {children}
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default DrawerLayout;
